import PropTypes from 'prop-types';
import { Routes, Route } from 'react-router-dom';
import ListOfCategoriesWithDeals from '../ListOfCategoriesWithDeals';
import RootLayout from '../RootLayout';
import Deal from '../Deal';
import HealthyDealsDataContextProvider from '../HealthyDealsDataContextProvider';
import { UrqlRender } from '../../shared/storybookRender';
import { componentMainTitle } from '../../utils/miscConstants';

const healthyDealsMain = (
	redirectToResourceLibrary,
	isUserAuthenticated,
	redirectToLogin,
	redirectToNetworkSearch,
	setMainTitleTo,
	description,
	updatePageTitleAndDescription,
	componentRootPath,
) => (
	<HealthyDealsDataContextProvider
		redirectToResourceLibrary={redirectToResourceLibrary}
		isUserAuthenticated={isUserAuthenticated}
		redirectToLogin={redirectToLogin}
		redirectToNetworkSearch={redirectToNetworkSearch}
		setMainTitleTo={setMainTitleTo}
		updatePageTitleAndDescription={updatePageTitleAndDescription}
		componentRootPath={componentRootPath}
		description={description}
	>
		<Routes>
			<Route
				path="/"
				element={
					<RootLayout>
						<ListOfCategoriesWithDeals />
					</RootLayout>
				}
			>
				<Route index element={<ListOfCategoriesWithDeals />} />
			</Route>
			<Route path="/:dealType/:dealId" element={<Deal />} />
		</Routes>
	</HealthyDealsDataContextProvider>
);

export function HealthyDeals({
	setMainTitleTo = componentMainTitle,
	description = null,
	redirectToResourceLibrary = null,
	updatePageTitleAndDescription,
	apiEnvironment = null,
	ssId = null,
	hpCode = null,
	redirectToLogin = null,
	redirectToNetworkSearch = null,
	hasOwnProvider = false,
	componentRootPath,
}) {
	const isUserAuthenticated = !!ssId; // If ssId is present, user is authenticated
	return !hasOwnProvider ? (
		<UrqlRender apiEnvironment={apiEnvironment} ssId={ssId} hpCode={hpCode}>
			{healthyDealsMain(
				redirectToResourceLibrary,
				isUserAuthenticated,
				redirectToLogin,
				redirectToNetworkSearch,
				setMainTitleTo,
				description,
				updatePageTitleAndDescription,
				componentRootPath,
			)}
		</UrqlRender>
	) : (
		healthyDealsMain(
			redirectToResourceLibrary,
			isUserAuthenticated,
			redirectToLogin,
			redirectToNetworkSearch,
			setMainTitleTo,
			description,
			updatePageTitleAndDescription,
			componentRootPath,
		)
	);
}

HealthyDeals.propTypes = {
	/**
	 * 	Consumer can set a title for this component which would be the main title for the whole component.
	 */
	setMainTitleTo: PropTypes.string,
	/**
	 * 	Consumer can set a description if they want for Healthy Deals which shows up in landing page.
	 */
	description: PropTypes.string,
	/**
	 * 	Consumer has to pass in `redirectToResourceLibrary` callback to the main component if they own Resource Library. This callback would redirect or navigate users to `Resource Library` landing page.
	 */
	redirectToResourceLibrary: PropTypes.func,
	/**
	 * 	Consumer has to pass in `updatePageTitleAndDescription` function to the main component. Healthy Deals will call this callback function with title and description as arguments. Consumer should update the `<title>` tag text with title argument and update meta description content with description argument for page SEO and Accessibility reasons. `<meta name="description" content=`${description}`>`.
	 */
	updatePageTitleAndDescription: PropTypes.func.isRequired,
	/**
	 * Consumer has to pass in the apiEnvironment to let the component decide where to get it's data from
	 */
	apiEnvironment: PropTypes.string,
	/**
	 * Consumer has to pass in the ssId value to the component that lets the component know if the current user is logged in or not
	 */
	ssId: PropTypes.string,
	/**
	 * Consumer has to pass in the hpCode value to the component that lets the component know the what healthplan code is the current user is under
	 * This needed to pull in different deals the user qualifies for
	 * The presence or absence of this will also determine look and feel of Deal's details page to  render content appropriately for user who already logged in vs not.
	 */
	hpCode: PropTypes.string,
	/**
	 * 	Consumer has to pass in `redirectToLogin` callback to the main component. This callback would redirect or navigate users to Login page of the site.
	 */
	redirectToLogin: PropTypes.func,
	/**
	 * 	Consumer has to pass in `redirectToNetworkSearch` callback to the main component. This callback would redirect or navigate users to Network search page.
	 */
	redirectToNetworkSearch: PropTypes.func,
	/** Consumer can provide their own URQL provider if they have one or can choose to use that comes default with the component */
	hasOwnProvider: PropTypes.bool,
	/**
	 * 	This component has different routes. When user switches routes and bookmarks the route, visiting it again might lose the back link state functionality within the component. So, if component cannot find the state for back link it will use this path to set the back link's path.
	 */
	componentRootPath: PropTypes.string.isRequired,
};
